import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { WarningCircle } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/stores/authStore'
import { useBillingStore } from '@/stores/billingStore'
import { usePlanLapse } from '@/hooks/usePlanLapse'

/**
 * App-wide notice for an account whose plan has expired, shown to EVERY member:
 * a teammate whose actions are disabled deserves to see why, not to meet a bare
 * refusal. Only someone who can manage the subscription gets a Renew button;
 * everyone else is pointed at the owner.
 *
 * Also keeps the plan status fresh, because `authStore.planStatus` is otherwise
 * read once at login: without this a renewed account would stay "lapsed" (and
 * its controls disabled) until a full reload, and a plan that expired mid-session
 * would keep looking active. Refreshes on tab focus and whenever the subscription
 * the Billing store holds changes (a purchase, a renewal, a plan switch).
 */
const PlanLapsedBanner: React.FC = () => {
  const navigate = useNavigate()
  const { lapsed, canRenew } = usePlanLapse()
  const fetchPlanStatus = useAuthStore((s) => s.fetchPlanStatus)
  const subscriptionKey = useBillingStore((s) =>
    s.subscription ? `${s.subscription.plan?.id}:${s.subscription.status}:${s.subscription.current_period_end}` : '',
  )

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchPlanStatus()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchPlanStatus])

  useEffect(() => {
    if (subscriptionKey) void fetchPlanStatus()
  }, [subscriptionKey, fetchPlanStatus])

  if (!lapsed) return null

  return (
    <div
      role="alert"
      data-testid="plan-lapsed-banner"
      className="flex flex-wrap items-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-6 py-2.5 text-amber-900 dark:border-amber-400/20 dark:bg-amber-400/[0.08] dark:text-amber-200 sm:px-8 lg:px-10"
      style={{ fontSize: '13px' }}
    >
      <WarningCircle className="h-4 w-4 shrink-0" weight="fill" />
      <p className="min-w-0 flex-1">
        <span style={{ fontWeight: 510 }}>Your plan has expired.</span>{' '}
        Campaigns are paused and AI, enrichment and plan features are switched off until it is renewed.
        {canRenew ? '' : ' Ask the account owner to renew it.'}
      </p>
      {canRenew && (
        <Button size="sm" onClick={() => navigate('/billing')}>
          Renew plan
        </Button>
      )}
    </div>
  )
}

export default PlanLapsedBanner
